/* eslint-disable custom-rules/zod-enforce-nullish */
import { z } from 'zod'

import { addressFormDataSchema } from './components/AddressSelector/schema'

export const unLocodeSchema = z
  .string()
  .regex(/^[A-Z]{2}[\dA-Z]{3}$/, {
    message: 'locations.unLocode_format_error',
  })
  .or(z.literal(''))

export const locationFormDataSchema = z
  .object({
    address: addressFormDataSchema
      .nullable()
      .default(null)
      .superRefine((request, ctx) => {
        if (request === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'locations.search_address_required_error',
          })
        }
      }),
    locationName: z.string().min(1, 'locations.location_name_required_error'),
    notes: z.string().optional(),
    companyName: z.string().optional(),
    contact: z.array(z.string()).optional(),
    unLocode: unLocodeSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (!!data.address?.address.countryCode && !!data.unLocode) {
      const addressCountryCode = data.address.address.countryCode?.toUpperCase()
      const unLocodeCountryCode = data.unLocode.substring(0, 2).toUpperCase()
      if (addressCountryCode !== unLocodeCountryCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['unLocode'],
          message: `locations.unLocode_country_code_mismatch_error`,
        })
      }
    }
  })

import {
  AddressResponseV1,
  AddressSource,
  CoordinatePrecision,
  CoordinatesResponseV1,
  PickupDeliveryLocationDeactivationReason,
  PickupDeliveryLocationDeleteReason,
  PickupDeliveryLocationListResponseV1,
  PickupDeliveryLocationResponseV1,
  PickupDeliveryLocationStatus,
  PickupDeliveryLocationType,
  PickupDeliveryLocationValidationType,
} from '@forto-dev/customer-master-data-models'

export const CoordinatesResponseV1Schema: z.ZodType<CoordinatesResponseV1> = z.object({
  lat: z.number(),
  lng: z.number(),
  precision: z.nativeEnum(CoordinatePrecision),
})

export const AddressResponseV1Schema: z.ZodType<AddressResponseV1> = z.object({
  companyName: z.string().nullable(),
  route1: z.string().nullable(),
  route2: z.string().nullable(),
  route3: z.string().nullable(),
  route4: z.string().nullable(),
  additionalInformation: z.string().nullable(),
  locality1: z.string().nullable(),
  locality2: z.string().nullable(),
  locality3: z.string().nullable(),
  locality4: z.string().nullable(),
  region1: z.string().nullable(),
  region2: z.string().nullable(),
  region3: z.string().nullable(),
  region4: z.string().nullable(),
  postalCode: z.string().nullable(),
  coordinates: CoordinatesResponseV1Schema.nullable(),
  countryName: z.string(),
  countryCode: z.string(),
  locationRef: z.string().nullable(),
  source: z.nativeEnum(AddressSource),
  hasCompleteAddress: z.boolean(),
})

export const PickupDeliveryLocationResponseV1Schema: z.ZodType<PickupDeliveryLocationResponseV1> = z.object({
  id: z.string(),
  accountID: z.string(),
  organizationID: z.string().nullable(),
  locationName: z.string(),
  address: AddressResponseV1Schema,
  addressFormatted: z.string(),
  localAddress: AddressResponseV1Schema.nullable(),
  localAddressFormatted: z.string().nullable(),
  locationType: z.array(z.nativeEnum(PickupDeliveryLocationType)),
  notes: z.string().nullable(),
  unLocode: z.string().nullable(),
  contactIDs: z.array(z.string()),
  legalEntityIDs: z.array(z.string()),
  validationType: z.nativeEnum(PickupDeliveryLocationValidationType),
  status: z.nativeEnum(PickupDeliveryLocationStatus),
  inactiveReason: z.nativeEnum(PickupDeliveryLocationDeactivationReason).nullable(),
  references: z.array(z.string()),
  successorLocationID: z.string().nullable(),
  predecessorLocationIDs: z.array(z.string()),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  deletedBy: z.string().nullable(),
  deletedAt: z.string().nullable(),
  deleteReason: z.nativeEnum(PickupDeliveryLocationDeleteReason).nullable(),
})

// TODO: We should generate this schema from the API response model instead of defining it manually
export const PickupDeliveryLocationListResponseV1Schema: z.ZodType<PickupDeliveryLocationListResponseV1> = z.object({
  items: z.array(PickupDeliveryLocationResponseV1Schema),
  total: z.number(),
})

// Exported inferred types for cross domain usage
export type PickupDeliveryLocation = z.infer<typeof PickupDeliveryLocationResponseV1Schema>

export type LocationFormData = z.infer<typeof locationFormDataSchema>
